import React from "react";
import { Box, Chip, Paper, Typography } from "@mui/material";
import { RegressionScenario } from "./regression.types";
import { regressionBucket, regressionStatusIcon, REGRESSION_COLORS } from "./regressionStatus";

interface Props {
  scenarios: RegressionScenario[];
}

const fmtMs = (ms: number) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`);

export const RegressionFailureCards: React.FC<Props> = ({ scenarios }) => {
  const needsAttention = scenarios.filter((s) => s.result !== "SUCCESS");

  if (needsAttention.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nothing needs attention — every scenario in the latest run passed.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {needsAttention.map((scenario) => (
        <Paper key={scenario.scenarioId} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap", mb: 1 }}>
            <Typography fontWeight={600}>{scenario.name}</Typography>
            <Typography variant="caption" color="text.secondary" fontFamily="monospace">{scenario.featurePath}</Typography>
          </Box>

          {scenario.steps && scenario.steps.length > 0 && (
            <Box
              component="pre"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.72rem",
                lineHeight: 1.7,
                bgcolor: "action.hover",
                border: "1px solid",
                borderColor: "divider",
                borderLeft: `3px solid ${REGRESSION_COLORS.failed}`,
                borderRadius: "0 6px 6px 0",
                p: 1.25,
                overflowX: "auto",
                whiteSpace: "pre-wrap",
                m: 0,
                mb: 1.25,
              }}
            >
              {scenario.steps.map((step, idx) => {
                const isFailing = step.result !== "SUCCESS";
                return (
                  <Box
                    key={idx}
                    component="div"
                    sx={{ color: isFailing ? REGRESSION_COLORS.failed : "text.secondary", fontWeight: isFailing ? 600 : 400 }}
                  >
                    {isFailing ? "✗" : "✓"} {step.description} — {fmtMs(step.durationMs)}
                  </Box>
                );
              })}
              {scenario.failureMessage && (
                <Box component="div" sx={{ color: REGRESSION_COLORS.failed, mt: 0.5 }}>
                  {scenario.failureMessage}
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
            {regressionStatusIcon(scenario.result)}
            <Chip
              label={scenario.result}
              size="small"
              sx={{ bgcolor: `${REGRESSION_COLORS[regressionBucket(scenario.result)]}22`, color: REGRESSION_COLORS[regressionBucket(scenario.result)], fontWeight: 600 }}
            />
            {scenario.tags.map((tag) => (
              <Chip key={tag} label={`@${tag}`} size="small" variant="outlined" sx={{ fontFamily: "monospace" }} />
            ))}
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

export default RegressionFailureCards;
